import {env} from "../../env/env";
import {DesignDto} from "../../generated/dashboard-client";
import {Button, Checkbox, Dropdown, Input, message, Drawer} from "antd";
import {DownOutlined, EditOutlined, MoreOutlined} from "@ant-design/icons";
import "./DesignCard.scss";
import React, {useState, useContext, useEffect, useRef} from "react";
import DesignApi from "../../api/DesignApi";
import {AppContext} from "../../AppContext";
import DesignCardMenu, {DesignOption, MenuMode} from "./DesignCardMenu";
import useFocus from "../../hooks/useFocus";
import {InUseModal} from "./InUseModal";
import {DuplicateNameModal} from "./DuplicateNameModal";
import {DesignInCartModal} from "./DesignInCartModal";
import {DesignInPrintProductionModal} from "./DesignInPrintProductionModal";
import {useRecoilState} from "recoil";
import {cartState, memberState, printProductionState} from "../../state/atoms";
import {isInUse} from "../../service/DesignService";
import {Spinner} from "../account/misc/Spinner";
import UserIcon from "../icons/UserIcon";
import {GetDesignEditLinkModal} from "./GetDesignEditLinkModal";

export interface DesignCardProps {
  design: DesignDto;
  hasRemoveFolderOption: boolean;
  onSelect: (design: DesignDto, checked: boolean) => void;
  selectMode: boolean;
  isSelected: boolean;
  onOptionClick: (design: DesignDto, option: DesignOption) => void;
  onUpdate: (design: DesignDto) => void;
  reloadDesigns: (data: any) => void;
  scrollToDesignsTop: () => void;
  isSingleUserAcc: boolean;
  getDesignThumb: (design: DesignDto) => string;
  hasUsers: boolean;
}

export default function DesignCard({
                                     design,
                                     onSelect,
                                     selectMode,
                                     onOptionClick,
                                     onUpdate,
                                     hasRemoveFolderOption,
                                     isSelected,
                                     reloadDesigns,
                                     scrollToDesignsTop,
                                     isSingleUserAcc,
                                     getDesignThumb,
                                     hasUsers
                                   }: DesignCardProps) {
  const dropdownButton = useRef<HTMLButtonElement>(null);
  const {location} = useContext(AppContext);
  const [cart] = useRecoilState(cartState);
  const [printProduction] = useRecoilState(printProductionState);
  const thumbRef = useRef<HTMLImageElement>(null);
  const [mode, setMode] = useState(MenuMode.Root);
  const [renameInputRef, setRenameInputFocus] = useFocus();
  const [isRenaming, setRenaming] = useState(false);
  const [name, setName] = useState("");
  const [isCopyLoading, setCopyLoading] = useState(false);
  const [isMenuVisible, setMenuVisible] = useState(false);
  const [isMobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [isInUseModalVisible, setInUseModalVisible] = useState(false);
  const [isDuplicateNameModalVisible, setDuplicateNameModalVisible] = useState(false);
  const [designInCartModal, setDesignInCartModal] = useState(false);
  const [designInPrintProductionModal, setDesignInPrintProductionModal] = useState(false);
  const [isGetDesignEditLinkModal, setDesignEditLinkModal] = useState(false);
  const [member] = useRecoilState(memberState);
  const [isInPrintProduction, setIsInPrintProduction] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setName(design.name ?? "");
  }, [design]);

  useEffect(() => {
    setIsInPrintProduction(printProduction.menus.indexOf(design.id!) > -1);
  }, [printProduction.menus, design.id])

  function onDropdownVisibilityChange(visible: boolean) {
    if (visible) {
      setMode(MenuMode.Root);
    }
  }

  function rename() {
    setRenaming(false);
    if (!!name) {
      DesignApi.rename(design.id!, name, location.internalId!).then(() => {
        onUpdate({...design, name});
      }).catch((err) => {
        if (err.response.status === 400) {
          setDuplicateNameModalVisible(true);
        } else {
          setName(design.name!);
        }
      });
    }
  }

  function closeDuplicateNameModal() {
    setDuplicateNameModalVisible(false);
    setName(design.name!);
  }

  function startRenaming() {
    if (!selectMode) {
      setRenaming(true);
      (setRenameInputFocus as () => void)();
    }
  }

  async function editDesign() {
    if (design.upload || !design.id) return;
    if (selectMode) {
      onSelect(design, !isSelected);
      return;
    }

    if (!isSingleUserAcc) {
      let inUse = isInUse(design, member);
      if (!inUse) {
        setLoading(true);
        const updatedDesign = (await DesignApi.getLightDesign(design.id)).data;
        setLoading(false);
        inUse = isInUse(updatedDesign, member);
        onUpdate(updatedDesign);
      }
      if (inUse) {
        setInUseModalVisible(true);
        return;
      }
    }

    if (cart.menus.indexOf(design.id!) > -1) {
      setDesignInCartModal(true);
      return;
    }

    if (isInPrintProduction) {
      setDesignInPrintProductionModal(true);
      return;
    }

    window.location.href = `${env.timberContextPath}/design?id=${design.id}`;
  }

  function closeInUseModal() {
    setInUseModalVisible(false);
  }

  function forceOpen() {
    window.location.href = `${env.timberContextPath}/design?id=${design.id}`;
  }

  function onFailedThumb() {
    const img = thumbRef.current!;
    img.setAttribute("src", `${env.dashboardPath}/thumb-placeholder.png`);
    img.style.maxHeight = "15%";
  }

  function onCopyDesign() {
    setCopyLoading(true);
    DesignApi.copy(design.id!, `Copy of ${design.name}`).then((resp) => {
      setCopyLoading(false);
      scrollToDesignsTop();
      reloadDesigns(resp.data);
      setMenuVisible(false);
    }).catch(() => {
      setCopyLoading(false);
      message.error("Error copying design");
    });
  }

  function onCloseMobileMenu() {
    setMobileMenuVisible(false);
  }

  function hideDropdown() {
    dropdownButton.current?.click();
  }

  return (
    <li className={`design-card ${selectMode ? "sel-mode" : ""} ${design.upload ? "pdf-upload" : ""}`}>
      {!member.viewOnly && <Checkbox
        checked={isSelected}
        onChange={(ev) => onSelect(design, ev.target.checked)}
      />}
      {!member.viewOnly && <Dropdown
        className={"desktop-design-menu more-btn-ops"}
        visible={isMenuVisible}
        overlay={
          <DesignCardMenu
            hideDropdown={hideDropdown}
            design={design}
            hasRemoveFolderOption={hasRemoveFolderOption}
            onOptionClick={onOptionClick}
            mode={mode}
            setMode={setMode}
            isCopyLoading={isCopyLoading}
            onCopyDesign={onCopyDesign}
            closeMenu={() => setMenuVisible(false)}
            isInPrintProduction={isInPrintProduction}
            setDesignInPrintProductionModal={setDesignInPrintProductionModal}
            setDesignEditLinkModal={setDesignEditLinkModal}
            editDesign={editDesign}
            hasUsers={hasUsers}
          />
        }
        onVisibleChange={onDropdownVisibilityChange}
      >
        <MoreOutlined ref={dropdownButton} onClick={() => {
          setMenuVisible(!isMenuVisible)
        }}/>
      </Dropdown>}

      {!member.viewOnly && <div className={"mobile-design-menu more-btn-ops"}>
        <MoreOutlined onClick={() => {
          setMobileMenuVisible(!isMobileMenuVisible)
        }}/>
        <Drawer
          placement={"bottom"}
          visible={isMobileMenuVisible}
          onClose={onCloseMobileMenu}
          closeIcon={<DownOutlined/>}
          className={"mobile-bottom-menu"}
          height={"450px"}
        >
          <DesignCardMenu
            hideDropdown={hideDropdown}
            design={design}
            hasRemoveFolderOption={hasRemoveFolderOption}
            onOptionClick={onOptionClick}
            mode={mode}
            setMode={setMode}
            isCopyLoading={isCopyLoading}
            onCopyDesign={onCopyDesign}
            closeMenu={() => setMenuVisible(false)}
            isInPrintProduction={isInPrintProduction}
            setDesignInPrintProductionModal={setDesignInPrintProductionModal}
            setDesignEditLinkModal={setDesignEditLinkModal}
            editDesign={editDesign}
            hasUsers={hasUsers}
          />
        </Drawer>
      </div>}

      <div
        className={`wrap ${isSelected ? "selected" : ""}`}
        onClick={editDesign}
      >
        {loading && <div className={'spinner'}><Spinner/></div>}
        <img
          loading="lazy"
          alt={design.name}
          ref={thumbRef}
          onError={onFailedThumb}
          src={getDesignThumb(design)}
        />

        <div className={`in-use-overlay ${!isSingleUserAcc && isInUse(design, member) ? "in-use-show" : ""}`}>
          <div>{design.inUse !== "-" ? design.inUse : <UserIcon/>}</div>
        </div>
      </div>
      {!isRenaming && (
        <div
          className={`edit-name ${selectMode ? "disable-rename" : ""}`}
          onClick={startRenaming}
        >
          <h6>{design.name}</h6>
          <Button type={"link"}>
            <EditOutlined/>
          </Button>
        </div>
      )}
      <div
        className={`editing-name ${isRenaming && !selectMode ? "show" : ""}`}
      >
        <Input
          ref={renameInputRef}
          value={name}
          onChange={(ev) => setName(ev.target.value)}
          onBlur={rename}
        />
      </div>

      <InUseModal isVisible={isInUseModalVisible} onClose={closeInUseModal} onOpenAnyway={forceOpen}/>
      <DuplicateNameModal isVisible={isDuplicateNameModalVisible} onClose={closeDuplicateNameModal} name={name}/>
      <DesignInCartModal
        isVisible={designInCartModal}
        open={forceOpen}
        onClose={() => setDesignInCartModal(false)}/>
      <DesignInPrintProductionModal isVisible={designInPrintProductionModal}
                                    onClose={() => setDesignInPrintProductionModal(false)} open={forceOpen}/>
      <GetDesignEditLinkModal isVisible={isGetDesignEditLinkModal} onClose={() => setDesignEditLinkModal(false)}
                              design={design}/>
    </li>
  );
}
